/**
 * In-app notification center — query + mutation hooks (lane A2, 2026-10-04).
 *
 * Backend: backend/servers/social/routers/notifications.py (migration 049,
 * services/notifications.py). Every row is scoped to the caller (org +
 * principal) server-side. All notification queries live under the
 * `socialKeys.resource("notifications")` prefix so one invalidation refreshes
 * the badge and the open dropdown together.
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "./client";
import { socialKeys } from "./keys";
import { objectResponseGuard } from "./schemas";

export type NotificationKind =
  | "publish_failed"
  | "post_published"
  | "approval_requested"
  | "approval_decision"
  | "token_expiring"
  | "comments_digest"
  | "reviews_digest"
  | "approval_stale"
  | "render_finished"
  | "listening_alert"
  | "account_connected"
  | "report_delivery_failed"
  | "dm_attention"
  | "video_autopilot_blocked"
  | "video_drafts_ready"
  | "content_library_pending"
  | "content_library_digest"
  | "story_manual_due";

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  title: string;
  body: string;
  /** Same-app SPA path (e.g. `/social/posts/<id>`), or null. */
  href: string | null;
  data: Record<string, unknown>;
  client_brand_id: string | null;
  created_at: string;
  read_at: string | null;
}

export interface NotificationList {
  items: AppNotification[];
  unread_count: number;
  next_before: number | null;
}

/** Badge poll cadence. TanStack pauses interval refetches while the tab is hidden. */
export const NOTIFICATION_POLL_MS = 60_000;

export const notificationKeys = {
  all: socialKeys.resource("notifications"),
  unread: socialKeys.detail("notifications", "unread-count"),
  list: (limit: number) => socialKeys.list("notifications", { limit }),
};

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  publish_failed: "Publish failed",
  post_published: "Published",
  approval_requested: "Needs approval",
  approval_decision: "Approval update",
  token_expiring: "Account sign-in",
  comments_digest: "New comments",
  reviews_digest: "New reviews",
  approval_stale: "Approval overdue",
  render_finished: "Video render",
  listening_alert: "Listening",
  account_connected: "Account connected",
  report_delivery_failed: "Report email",
  dm_attention: "Direct message",
  video_autopilot_blocked: "Video Autopilot",
  video_drafts_ready: "Video draft ready",
  content_library_pending: "Content to review",
  content_library_digest: "Content Library",
  story_manual_due: "Story waiting to post",
};

export function useNotificationUnreadCount() {
  return useApiQuery<{ unread: number }>(notificationKeys.unread, "/api/notifications/unread-count", {
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    schema: objectResponseGuard<{ unread: number }>(),
  });
}

/** The newest `limit` notifications; only fetched while the dropdown is open. */
export function useNotificationList(enabled: boolean, limit = 20) {
  return useApiQuery<NotificationList>(notificationKeys.list(limit), "/api/notifications", {
    enabled,
    axiosConfig: { params: { limit } },
    refetchInterval: enabled ? NOTIFICATION_POLL_MS : false,
    refetchIntervalInBackground: false,
    schema: objectResponseGuard<NotificationList>(),
  });
}

/** Mark notifications read — `{ ids }` for specific rows or `{ all: true }`. */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { ids: number[] } | { all: true }) => {
      const res = await client.post<{ success: boolean; data: { updated: number; unread: number } }>(
        "/api/notifications/read",
        body
      );
      if (!res.data?.success) throw new Error("Couldn't update notifications. Please try again.");
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationKeys.unread, { unread: data.unread });
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
